import { CheckIcon } from '@heroicons/react/16/solid';
import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { Badge } from '@/components/ui/daisy/badge';
import { Dropdown } from '@/components/ui/daisy/dropdown';
import Fieldset from '@/components/ui/daisy/fieldset';
import { Label } from '@/components/ui/daisy/label';
import { Menu } from '@/components/ui/daisy/menu';
import { Colors, Sizes } from '@/components/ui/types';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import {
  BadgeSelectContextValue,
  BadgeSelectOptionProps,
  BadgeSelectProps,
} from './types';
import { useBadgeSelect, UseBadgeSelectParams } from './useBadgeSelect';
import { Button } from '../../button';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

const DEFAULT_MAX_VISIBLE_BADGES = 6;

const BadgeSelectContext = createContext<BadgeSelectContextValue>({
  value: undefined,
  multiple: false,
  onSelect: () => {},
  size: 'md',
  registerOption: () => {},
  unregisterOption: () => {},
});

const useBadgeSelectContext = () => useContext(BadgeSelectContext);

const sizeClasses: Record<Sizes, string> = {
  xs: 'select-xs',
  sm: 'select-sm',
  md: 'select-md',
  lg: 'select-lg',
  xl: 'select-xl',
};

const colorClasses: Record<Colors, string> = {
  default: '',
  neutral: 'select-neutral',
  primary: 'select-primary',
  secondary: 'select-secondary',
  accent: 'select-accent',
  info: 'select-info',
  success: 'select-success',
  warning: 'select-warning',
  error: 'select-error',
};

const BadgeSelectBase = forwardRef<HTMLDivElement, BadgeSelectProps>(
  (props, ref) => {
    const {
      multiple,
      value,
      onChange,
      maxVisibleBadges = DEFAULT_MAX_VISIBLE_BADGES,
      placeholder = 'Selecione',
      disabled = false,
      error = false,
      size = 'md',
      color = 'default',
      forceOpen = false,
      onClear,
      clearAriaLabel,
      label,
      name,
      classNames,
      children,
      ...divProps
    } = props;

    const { t } = useTranslation('translation', {
      keyPrefix: 'common',
    });

    const [isOpen, setIsOpen] = useState(forceOpen);

    useEffect(() => {
      setIsOpen(forceOpen);
    }, [forceOpen]);

    const hookParams = {
      multiple: !!multiple,
      onChange,
      value,
      disabled,
    };

    const {
      selectedValues,
      selectedOptions,
      handleSelect: baseHandleSelect,
      registerOption,
      unregisterOption,
    } = useBadgeSelect(hookParams as UseBadgeSelectParams);

    const handleSelect = (optionValue: string) => {
      baseHandleSelect(optionValue);
      if (!multiple) setIsOpen(false);
    };

    const visibleOptions = selectedOptions.slice(0, maxVisibleBadges);
    const hiddenCount = selectedOptions.length - visibleOptions.length;

    return (
      <BadgeSelectContext.Provider
        value={{
          value,
          multiple: !!multiple,
          onSelect: handleSelect,
          size,
          registerOption,
          unregisterOption,
        }}
      >
        <Dropdown
          ref={ref}
          open={isOpen}
          className={twMerge('w-full', classNames?.base)}
          {...divProps}
        >
          <ConditionalRenderer condition={!!name}>
            <ConditionalRenderer
              condition={selectedValues.length > 0}
              fallback={<input type="hidden" name={name} value="" />}
            >
              {selectedValues.map(v => (
                <input key={v} type="hidden" name={name} value={v} />
              ))}
            </ConditionalRenderer>
          </ConditionalRenderer>

          <Dropdown.Trigger disabled={disabled} className="relative">
            <label
              className={twMerge(
                'select w-full cursor-pointer flex items-center',
                sizeClasses[size],
                colorClasses[color],
                error && 'select-error',
                disabled && 'select-disabled pointer-events-none',
              )}
            >
              {label && (
                <Label as="span" className={classNames?.label}>
                  {label}
                </Label>
              )}
              <ConditionalRenderer
                condition={selectedOptions.length > 0}
                fallback={
                  <span className="text-base-content/50">{placeholder}</span>
                }
              >
                <div className="flex flex-wrap gap-1 items-center">
                  {visibleOptions.map(option => (
                    <Badge
                      key={option.value}
                      color={option.color}
                      variant={option.variant}
                      size={size}
                    >
                      {option.children}
                    </Badge>
                  ))}
                  <ConditionalRenderer condition={hiddenCount > 0}>
                    <Badge variant="ghost" size={size}>
                      +{hiddenCount}
                    </Badge>
                  </ConditionalRenderer>
                </div>
              </ConditionalRenderer>
            </label>
            <ConditionalRenderer
              condition={!!onClear && selectedValues.length > 0 && !disabled}
            >
              <Button
                aria-label={clearAriaLabel || t('clear')}
                className="absolute right-10 top-1/2 -translate-y-1/2"
                variant="ghost"
                size="xs"
                shape="circle"
                data-testid="badge-select-clear"
                type="button"
                tabIndex={-1}
                onMouseDown={event => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  onClear?.();
                }}
              >
                <XMarkIcon className="size-4 text-error" />
              </Button>
            </ConditionalRenderer>
          </Dropdown.Trigger>

          <Dropdown.Content className="z-10 w-full!">
            <Menu
              role="listbox"
              className={twMerge(
                'w-full bg-base-100 rounded-box shadow-sm p-2 max-h-60 overflow-y-auto',
                classNames?.listbox,
              )}
              activeColor="neutral"
            >
              {children}
            </Menu>
          </Dropdown.Content>
        </Dropdown>
      </BadgeSelectContext.Provider>
    );
  },
);

const Option = ({
  value,
  children,
  color = 'default',
  variant = 'soft',
  disabled = false,
}: BadgeSelectOptionProps) => {
  const {
    value: selectedValue,
    multiple,
    onSelect,
    registerOption,
    unregisterOption,
    size,
  } = useBadgeSelectContext();

  const isSelected = multiple
    ? Array.isArray(selectedValue) && selectedValue.includes(value)
    : selectedValue === value;

  const childrenRef = useRef(children);
  childrenRef.current = children;

  useEffect(() => {
    registerOption(value, { children: childrenRef.current, color, variant });
    return () => unregisterOption(value);
  }, [value, color, variant, registerOption, unregisterOption]);

  const isDisabled = disabled || (!multiple && isSelected);

  return (
    <Menu.Item role="option" aria-selected={isSelected} disabled={isDisabled}>
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => onSelect(value)}
        className="flex items-center"
      >
        <ConditionalRenderer
          condition={isSelected}
          fallback={<div className="size-4" />}
        >
          <CheckIcon className="size-4 text-base-content/50" />
        </ConditionalRenderer>

        <Badge color={color} variant={variant} size={size}>
          {children}
        </Badge>
      </button>
    </Menu.Item>
  );
};

BadgeSelectBase.displayName = 'BadgeSelect';
Option.displayName = 'BadgeSelect.Option';

export const BadgeSelect = Object.assign(BadgeSelectBase, {
  Option,
  Label,
  Fieldset,
});
